<template>
    <div v-if="loading" style="display:flex;justify-content:center;align-items:center;height:100vh;">
        <Icon name="loader" class="animate-spin" :size="32" />
    </div>
    <div v-else-if="!authenticated && authRequired" style="display:flex;justify-content:center;align-items:center;height:100vh;flex-direction:column;gap:1rem;">
        <Icon name="lock" :size="48" style="color:var(--jb-primary);" />
        <h2>Session expired</h2>
        <p>Redirecting to sign-in…</p>
    </div>
    <!-- A div, not a <template>: this branch has several children, and a
         multi-child template root makes the whole component a fragment, which
         <Transition> in App.vue cannot animate. The route's out-in leave then
         never completes and the page is left blank. Keeping every branch a single
         element is what avoids that. -->
    <div v-else>
        <!-- Public routes (landing, register, login) render edge-to-edge without
             the employer-section chrome. Toast + confirm modals stay attached.
             The primary navbar lives in _Layout.cshtml (shared JobsProfileNavbar),
             so this layout only adds the employer section tab strip + pending banner. -->
        <employer-subnav v-if="showChrome" />
        <pending-account-banner v-if="showChrome" />
        <div :class="showChrome ? 'emp-page-wrap' : ''">
            <router-view v-slot="{ Component, route }">
                <transition :name="(route.meta.transition as string) || 'page-fwd'"
                            mode="out-in"
                            @leave="onRouteLeave">
                    <component :is="Component" />
                </transition>
            </router-view>
        </div>
        <toast-container />
        <confirm-modal />
    </div>
</template>

<script lang="ts">
// employer.css (.emp-*/.adp-*) styles the entire dashboard subtree; importing
// it once here covers every child route's components in one shared chunk.
import '../../css/employer.css';

import { defineComponent, reactive, provide, computed } from 'vue';
import EmployerSubnav from '../components/employer/EmployerSubnav.vue';
import PendingAccountBanner from '../components/employer/PendingAccountBanner.vue';
import ToastContainer from '../components/ToastContainer.vue';
import ConfirmModal from '../components/ConfirmModal.vue';
import { useEmployerSessionStore } from '../stores/employerSession';
import { onRouteLeave } from '../lib/route-transition';

declare const window: any;

const BASE = (window.__APP_BASE__ || '/').replace(/\/+$/, '') + '/';

// Routes that render without the employer chrome (navbar, pending banner) and
// without requiring an authenticated EmployerSession.
const PUBLIC_ROUTES = ['employer-landing', 'employer-onboarding'];

// Without these the tab kept the marketing page's title across the whole employer area.
const PAGE_TITLES: Record<string, string> = {
    'employer-onboarding': 'Register as an employer',
    'employer-dashboard': 'Dashboard',
    'employer-inbox': 'Applicants',
    'employer-messages': 'Messages',
    'employer-candidates': 'Talent pool',
    'employer-profile': 'Company',
    'employer-edit-listing': 'Edit listing',
    'employer-team': 'Team',
    'employer-billing': 'Billing',
};

export default defineComponent({
    name: 'EmployerLayout',
    components: {
        'employer-subnav': EmployerSubnav,
        'pending-account-banner': PendingAccountBanner,
        'toast-container': ToastContainer,
        'confirm-modal': ConfirmModal,
    },
    setup() {
        const basePath = (window.__APP_BASE__ || '').replace(/\/+$/, '');
        const profileUrl = (window.__PROFILE_URL__ || '/profile').replace(/\/+$/, '');
        const jobsUrl = basePath || '/';
        const jobsRoot = basePath;

        // companyState lives on for backward compatibility with child components
        // that inject it directly. New code should prefer the Pinia store.
        const companyState = reactive({
            name: '',
            logoUrl: '',
            isVerified: false,
        });

        const session = useEmployerSessionStore();

        const toastState = reactive({ toasts: [] as any[], nextId: 1 });
        // action is optional: { label, run }. Used for undo on destructive bulk
        // work, where the toast is the only place a reversal can live without
        // adding a second banner to the page.
        function addToast(message: string, type = 'info', duration = 4000,
                          action?: { label: string; run: () => void }) {
            const id = toastState.nextId++;
            toastState.toasts.push({ id, message, type, duration, action });
            setTimeout(() => {
                toastState.toasts = toastState.toasts.filter(t => t.id !== id);
            }, duration);
        }

        const confirmState = reactive({ show: false, message: '', resolve: null as any });
        function showConfirm(message: string): Promise<boolean> {
            return new Promise(resolve => {
                confirmState.show = true;
                confirmState.message = message;
                confirmState.resolve = resolve;
            });
        }

        provide('basePath', basePath);
        provide('profileUrl', profileUrl);
        provide('jobsUrl', jobsUrl);
        provide('jobsRoot', jobsRoot);
        provide('companyState', companyState);
        provide('toast', addToast);
        provide('confirm', showConfirm);
        provide('toastState', toastState);
        provide('confirmState', confirmState);

        return { basePath, companyState, toastState, confirmState, session, onRouteLeave };
    },
    data() {
        // Public routes must not render the loading spinner even for one frame:
        // /employer is server-prerendered, and a spinner frame between the
        // static paint and the Vue render is a visible white flash. The route
        // is already matched when the layout renders, so this is safe.
        const isPublic = PUBLIC_ROUTES.includes(this.$route.name as string);
        return {
            loading: !isPublic,
            authenticated: isPublic,
        };
    },
    computed: {
        authRequired(): boolean {
            return !PUBLIC_ROUTES.includes(this.$route.name as string);
        },
        showChrome(): boolean {
            // Employer-section chrome (navbar, pending banner) shown on every
            // authenticated employer route. Public routes (landing, register,
            // login) render standalone.
            return this.authenticated && this.authRequired;
        },
    },
    watch: {
        // Guard approval-gated routes client-side. Server still enforces via
        // Status=Pending and disabled actions, but surfacing it in-SPA avoids
        // a route that wouldn't work anyway.
        '$route.name'() {
            this.setPageTitle();
            this.enforceApprovalGuard();
        },
    },
    async mounted() {
        this.setPageTitle();
        // Public routes bypass the auth dance entirely. Registered employers
        // hitting the landing are already forwarded to /dashboard by the
        // router beforeEach guard.
        if (!this.authRequired) {
            this.loading = false;
            this.authenticated = true;
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');

        if (token) {
            try {
                const res = await fetch(BASE + 'api/employer/auth-token', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                });
                const data = await res.json();
                if (data.success) {
                    // Strip token BEFORE re-fetching session so the URL looks clean.
                    window.history.replaceState({}, '', window.location.pathname);
                    await this.hydrateSession();
                } else {
                    this.redirectToLogin();
                    return;
                }
            } catch {
                this.redirectToLogin();
                return;
            }
        } else {
            const ok = await this.hydrateSession();
            if (!ok) {
                this.redirectToLogin();
                return;
            }
        }

        this.loading = false;
        this.enforceApprovalGuard();
    },
    methods: {
        async hydrateSession(): Promise<boolean> {
            const payload = await this.session.fetchSession(this.basePath);
            if (!payload.authenticated) return false;
            this.companyState.name = payload.companyName ?? '';
            this.companyState.logoUrl = payload.logoUrl ?? '';
            this.companyState.isVerified = !!payload.isVerified;
            this.authenticated = true;
            return true;
        },
        setPageTitle() {
            const page = PAGE_TITLES[this.$route.name as string];
            if (page) document.title = `${page} | Scholaro Jobs for Employers`;
        },
        enforceApprovalGuard() {
            if (!this.authenticated) return;
            const needsApproval = (this.$route.meta as any)?.requiresApproval === true;
            if (needsApproval && !this.session.isApproved) {
                this.$router.replace({ name: 'employer-dashboard' });
            }
        },
        redirectToLogin() {
            this.loading = false;
            this.authenticated = false;
            setTimeout(() => {
                // All employer auth flows through Scholaro's /Login now. We round-
                // trip through /employer/enter — that's the endpoint that matches
                // the Scholaro user to an Employers row (by SupabaseUserId or
                // email) and either issues the EmployerSession cookie or kicks to
                // /employer/onboarding if they're not yet onboarded.
                const basePath = this.basePath || '';
                const returnUrl = encodeURIComponent(basePath + '/employer/enter');
                window.location.href = '/Login?ReturnUrl=' + returnUrl;
            }, 400);
        },
    },
});
</script>
